import type { ComplianceState } from '../../types/compliance';

/**
 * Decisions behind the death-notice and records-export controls on the client
 * compliance panel. Pure, so each rule is tested without a DOM.
 */

/**
 * What the panel says about a death notice. It is read from the notice itself (the
 * compliance state carries it), never inferred from the freeze: a notice recorded on an
 * account already frozen for another reason is in force and places no freeze of its own.
 */
export type DeathNoticeView =
  | { kind: 'unreadable' }
  | { kind: 'none' }
  /** `recordedByViewer`: the person looking recorded it, so someone else releases it. */
  | { kind: 'active'; recordedByViewer: boolean };

export function deathNoticeView(
  state: ComplianceState | null,
  viewerId: string | undefined,
): DeathNoticeView {
  if (state === null) return { kind: 'unreadable' };
  if (!state.death_notice_active) return { kind: 'none' };
  return {
    kind: 'active',
    recordedByViewer: viewerId !== undefined && state.death_notice_recorded_by === viewerId,
  };
}

export const RELEASE_REASON_MIN = 5;
export const KYC_EXPORT_REASON_MIN = 10;
export const PERSONAL_EXPORT_REFERENCE_MIN = 5;

/** Mirrors the API: the regulator expediente is super_admin only. */
export function canExportKycFile(role: string | undefined): boolean {
  return role === 'super_admin';
}

/** Mirrors the API: the data-subject answer is manager and super_admin. */
export function canExportPersonalData(role: string | undefined): boolean {
  return role === 'manager' || role === 'super_admin';
}

/** Why the record button is still off, in words for the screen; null when it can go. */
export function recordBlockedReason(confirmed: boolean): string | null {
  return confirmed ? null : 'Tick the confirmation to record the notice.';
}

/** Why the release button is still off; null when it can go. */
export function releaseBlockedReason(reason: string): string | null {
  return reason.trim().length >= RELEASE_REASON_MIN
    ? null
    : `Write a reason of at least ${RELEASE_REASON_MIN} characters to release the freeze.`;
}

/** Why an export button is still off; null when it can go. */
export function exportBlockedReason(text: string, min: number, what: string): string | null {
  return text.trim().length >= min
    ? null
    : `Enter ${what} of at least ${min} characters to export.`;
}

/** Hands a fetched file to the browser as a download. Nothing is put in a URL but the local blob. */
export function saveBlob(blob: Blob, filename: string): void {
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(href);
}
